import { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useModal } from '@/providers/modal';
import { useGetFundraisingApplicationList } from '@/query/queries/fundraising/useGetFundraisingApplicationList.js';
import { useCreateFundraisingApplication } from '@/query/queries/fundraising/useCreateFundraisingApplication';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { prettyDate } from '@/helpers/date';
import IconActive from './icon_on.svg';
import IconInActive from './icon_off.svg';
import r from '@/kit/RoundButton/button.module.scss';
import s from './index.module.scss';

const id = MODAL_IDS.application_select_modal;

export const ApplicationSelectModal = props => {
  const { modalIds } = useModal();
  return modalIds[id] && <Modal {...props} />;
};

const Modal = ({ onSuccess }) => {
  const { closeModal } = useModal();
  const { t } = useTranslation();
  const { data } = useGetFundraisingApplicationList();
  const { mutateAsync: createApp } = useCreateFundraisingApplication();
  const [fundraisingId, setFundraisingId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (data && !fundraisingId) {
      setFundraisingId(data.filter(app => app.status === null)[0]?.fundraising_id);
    }
  }, [data, fundraisingId]);

  const getRoundInfo = (startDate, endDate, index) => {
    const period = [prettyDate(startDate, 'DD'), prettyDate(endDate, 'DD MMM')].join('-')
    if (index > 0) {
      return t('modal:application_select_modal.next_round', { period });
    }
    if (index === 0) {
      return t('modal:application_select_modal.upcoming_round', { period });
    }
  }

  const onClose = () => closeModal(id);

  const onSubmit = async () => {
    try {
      setLoading(true);
      const res = await createApp({ fundraising_id: fundraisingId });
      if (res) {
        onSuccess && onSuccess(res);
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <div className={s.modal}>
      <div className={s.header}>
        <div className="f-hero-h5">{t('modal:application_select_modal.new_application')}</div>
        <div className="f-small-regular">{t('modal:application_select_modal.choose_round')}</div>
      </div>
      <div className={s.content}>
        {data?.map((app, idx) => {
          const isSelected = fundraisingId === app.fundraising_id;
          const disabled = app.status !== null;

          return (
            <div
              key={app.fundraising_id}
              className={classnames(
                s.card,
                isSelected && s.card_active,
                disabled && s.card_disabled,
              )}
              onClick={() => setFundraisingId(app.fundraising_id)}
            >
              <div className="f-body-medium">
                {t('modal:application_select_modal.round', { round: app.fundraising_name })}
              </div>
              <div className="f-label">{getRoundInfo(app.start_date, app.end_date, idx)}</div>
              <img
                src={isSelected ? IconActive : IconInActive}
                alt="icon"
                width={20}
                height={20}
                className={s.card_icon}
              />
            </div>
          );
        })}
      </div>
      <div className={s.footer}>
        <button type="button" className={classnames(s.btn_close, r.grey_large)} onClick={onClose}>
          {t('modal:application_select_modal.cancel')}
        </button>
        <button
          type="button"
          className={classnames(s.btn_submit, r.black_large)}
          onClick={onSubmit}
          disabled={!fundraisingId || loading}
        >
          {loading
            ? t('modal:application_select_modal.creating')
            : t('modal:application_select_modal.create_application')}
        </button>
      </div>
    </div>
  );

  return (
    <ModalCenterLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalCenterLayout>
  );
};
